"use client";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { ArrowRight, ChevronDown, X, Phone } from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Brand Tokens (matches Navbar) ────────────────────────────────────────────
// Panel bg     : #FAFAF7  (warm off-white)
// Section bg   : #F5F4F0  (slightly deeper)
// Navy top bar : #0A1628
// Primary Blue : #1565C0
// Accent Orange: #C85D1A
// ─────────────────────────────────────────────────────────────────────────────

const social = [
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/farsight-solution",
    icon: (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    name: "Twitter",
    href: "https://twitter.com",
    icon: (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    name: "Instagram",
    href: "https://instagram.com",
    icon: (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
  },
];

const mainLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/blog", label: "Blog" },
  { href: "/contact", label: "Contact" },
  { href: "/careers", label: "Careers" },
];

const serviceGroups = [
  {
    title: "Business Consultancy",
    color: "bg-[#1565C0]/10 text-[#1565C0]",
    links: [
      { href: "/services", label: "Strategic Overview" },
      { href: "/about", label: "Growth Advisory" },
      { href: "/360-support", label: "360° Support" },
    ],
  },
  {
    title: "Funding",
    color: "bg-emerald-500/10 text-emerald-700",
    links: [
      { href: "/services/funding", label: "Funding Programs" },
      { href: "/services/funding/grants", label: "Grants" },
      { href: "/services/funding/loans", label: "NBFC & Loans" },
    ],
  },
  {
    title: "Certification",
    color: "bg-violet-500/10 text-violet-700",
    links: [
      { href: "/services/certification", label: "All types ISO certification" },
      { href: "/services/certification", label: "Startup India Certificate" },
      { href: "/services/certification", label: "Udhyam Registeration" },
      { href: "/services/certification", label: "GeM Registeration" },
      { href: "/services/certification", label: "Zed Certification" },
      { href: "/services/certification", label: "Section 80 IAC" },
      { href: "/services/certification", label: "FCRA Registration" },
      { href: "/services/certification", label: "12A & 80G Certification" },
    ],
  },
  {
    title: "Legal",
    color: "bg-amber-500/10 text-amber-700",
    links: [
      { href: "/services/legal", label: "Contracts & Policy" },
      { href: "/services/legal", label: "Corporate Governance" },
    ],
  },
  {
    title: "Marketing",
    color: "bg-rose-500/10 text-rose-700",
    links: [
      { href: "/services/marketing", label: "Brand & Growth" },
      { href: "/services/marketing", label: "Campaigns & Content" },
    ],
  },
  {
    title: "IT Solutions",
    color: "bg-cyan-500/10 text-cyan-700",
    links: [
      { href: "/services/it/software", label: "Software" },
      { href: "/services/it/appdev", label: "App Dev" },
      { href: "/services/it/cloud", label: "Cloud" },
      { href: "/services/it/webdev", label: "Web Dev" },
      { href: "/services/it/hosting", label: "Hosting" },
      { href: "/services/it/crm", label: "CRM" },
      { href: "/services/it/erp", label: "ERP" },
      { href: "/services/it/aiml", label: "AI/ML" },
      { href: "/services/it/cybersecurity", label: "Cybersecurity" },
      { href: "/services/it/devops", label: "DevOps" },
    ],
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
};

export function MobileMenu({ open, onClose }: Props) {
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="mobile-shell"
          className="fixed inset-0 z-[60] lg:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          {/* Backdrop */}
          <motion.button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-[#0A1628]/50 backdrop-blur-[3px]"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Slide-in panel */}
          <motion.aside
            className="absolute inset-y-0 right-0 flex w-full max-w-[400px] flex-col bg-[#FAFAF7] shadow-[−4px_0_40px_rgba(0,0,0,0.12)]"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 340 }}
          >

            {/* ── Header ───────────────────────────────────────────────────── */}
            <div className="flex items-center justify-between border-b border-black/[0.07] bg-[#FAFAF7] px-5 py-3">
              <Link href="/" onClick={onClose}>
                <Image
                  src="/images/logo.png"
                  alt="FarSight Solutions"
                  height={36}
                  width={120}
                  className="h-9 w-auto object-contain"
                  priority
                />
              </Link>
              <button
                type="button"
                aria-label="Close menu"
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-black/10 text-[#555] transition hover:bg-[#EDECE7] hover:text-[#1565C0]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Gradient accent line */}
            <div className="h-[2px] w-full shrink-0 bg-gradient-to-r from-[#1565C0] via-[#C85D1A] to-[#1565C0]" />

            {/* ── Scrollable body ──────────────────────────────────────────── */}
            <nav className="flex-1 overflow-y-auto px-5 py-5">

              {/* Main links */}
              <ul className="space-y-0.5">
                {mainLinks.map((item, i) => (
                  <motion.li
                    key={item.href}
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.04 + i * 0.04, duration: 0.2 }}
                  >
                    <Link
                      href={item.href}
                      onClick={onClose}
                      className="flex items-center rounded-lg px-3 py-2.5 text-[14px] font-medium text-[#2A2A2A] transition-colors hover:bg-[#EDECE7] hover:text-[#1565C0]"
                    >
                      {item.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>

              {/* Divider */}
              <div className="my-4 h-px bg-black/[0.06]" />

              {/* Services accordion */}
              <motion.div
                initial={{ opacity: 0, x: 14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.22, duration: 0.2 }}
              >
                <button
                  type="button"
                  onClick={() => setServicesOpen((v) => !v)}
                  aria-expanded={servicesOpen}
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-[14px] font-semibold transition-colors",
                    servicesOpen
                      ? "bg-[#EBF2FC] text-[#1565C0]"
                      : "text-[#2A2A2A] hover:bg-[#EDECE7]"
                  )}
                >
                  Services
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      servicesOpen ? "rotate-180 text-[#C85D1A]" : "text-black/30"
                    )}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {servicesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 space-y-4 rounded-xl border border-black/[0.06] bg-white p-4">
                        {serviceGroups.map((group, gi) => (
                          <div key={group.title}>
                            {/* Group heading */}
                            <div className="mb-2 flex items-center gap-2">
                              <span className={cn(
                                "inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                                group.color
                              )}>
                                {group.title}
                              </span>
                            </div>
                            {/* Sub-links */}
                            <ul className="space-y-0.5">
                              {group.links.map((link) => (
                                <li key={link.href + link.label}>
                                  <Link
                                    href={link.href}
                                    onClick={onClose}
                                    className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-[12.5px] text-[#555] transition-colors hover:bg-[#F5F4F0] hover:text-[#1565C0]"
                                  >
                                    <span className="h-px w-3 shrink-0 rounded-full bg-black/15" />
                                    {link.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                            {/* Divider between groups */}
                            {gi < serviceGroups.length - 1 && (
                              <div className="mt-3 h-px bg-black/[0.05]" />
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </nav>

            {/* ── Footer ───────────────────────────────────────────────────── */}
            <div className="shrink-0 border-t border-black/[0.07] bg-[#F5F4F0] px-5 py-5">

              {/* Phone */}
              <a
                href="tel:+919821605930"
                className="mb-3 flex items-center gap-2.5 rounded-lg border border-black/[0.07] bg-white px-4 py-2.5 text-[13px] font-medium text-[#444] transition hover:border-[#1565C0]/25 hover:text-[#1565C0]"
              >
                <Phone className="h-4 w-4 text-[#1565C0]" />
                +91 98216 05930
              </a>

              {/* Primary CTA */}
              <Link
                href="/contact"
                onClick={onClose}
                className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-[#C85D1A] px-5 py-3 text-center text-[13.5px] font-bold text-white shadow-[0_2px_14px_rgba(200,93,26,0.28)] transition hover:shadow-[0_4px_20px_rgba(200,93,26,0.40)]"
              >
                <span className="pointer-events-none absolute inset-0 -translate-x-full skew-x-12 bg-white/15 transition-transform duration-700 group-hover:translate-x-full" />
                Get Free Consultation
                <ArrowRight className="h-4 w-4" />
              </Link>

              {/* Social + tagline */}
              <div className="mt-4 flex items-center justify-between">
                <p className="text-[10px] font-medium tracking-wide text-black/30">
                  Follow us
                </p>
                <div className="flex items-center gap-1">
                  {social.map((s) => (
                    <a
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.name}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-black/[0.08] bg-white text-[#555] transition hover:border-[#1565C0]/30 hover:bg-[#EBF2FC] hover:text-[#1565C0]"
                    >
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>

          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}